/** What the console knows about the rank tracker's last snapshot.
 *
 *  The tracker keeps its last result in memory (`/api/tracking/status`), so a
 *  restart or a deploy empties it until the next run. The job history in the
 *  database does not forget: when the tracker's own answer is empty, the newest
 *  finished `tracking_snapshot` run stands in for it (plan 1-3). The same
 *  function feeds 오늘's status line and the attention items, so they never
 *  disagree. */

import type { JobRun, TrackingStatus } from '@/api/client'

export type TrackerKey = 'never' | 'running' | 'ok' | 'error'

export interface TrackerView {
  key: TrackerKey
  /** ISO time of the last finished snapshot, null before any. */
  lastAt: string | null
  /** The failure's own words, empty when it did not fail. */
  message: string
  /** Where `lastAt` came from: the tracker itself or the job history. */
  from: 'status' | 'jobs' | null
}

function hasTime(iso: string | undefined | null): iso is string {
  return !!iso && !iso.startsWith('0001-')
}

/** The newest snapshot run that actually ran (a skipped or cancelled one did
 *  not take a snapshot). `runs` may come in any order. */
export function lastSnapshotRun(runs: JobRun[] | null | undefined): JobRun | null {
  let best: JobRun | null = null
  for (const r of runs ?? []) {
    if (r.job !== 'tracking_snapshot' || (r.status !== 'ok' && r.status !== 'error')) continue
    if (!best || r.started_at > best.started_at) best = r
  }
  return best
}

export function trackerView(status: TrackingStatus | null | undefined, runs?: JobRun[] | null): TrackerView {
  const running = !!status?.running
  if (status && hasTime(status.last_at)) {
    return {
      key: running ? 'running' : status.last_ok ? 'ok' : 'error',
      lastAt: status.last_at,
      message: status.last_ok ? '' : status.last_msg,
      from: 'status',
    }
  }
  const run = lastSnapshotRun(runs)
  if (run) {
    return {
      key: running ? 'running' : run.status === 'ok' ? 'ok' : 'error',
      lastAt: run.finished_at ?? run.started_at,
      message: run.status === 'ok' ? '' : run.message,
      from: 'jobs',
    }
  }
  return { key: running ? 'running' : 'never', lastAt: null, message: '', from: null }
}
